import {Box, Button, TextField, Typography} from "@mui/material";
import Dialog from "../shared/Dialog";
import {useState, useEffect} from "react";
import {updateProgramWarmup} from "../../services/PlanSerice";
import TranslationDialogButton from "../translation/TranslationDialogButton";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";

/**
 * field key -> Hebrew label for the panel. PROGRAM declares name / description / warmupText; this
 * dialog only edits the warmup text, but the panel renders whatever the schema lists — the other
 * two are the program's own name and description, which are equally translatable and were simply
 * never editable anywhere. Labelling them costs nothing and mislabelling them would be worse.
 */
const PROGRAM_FIELD_LABELS = {
  name: "שם התוכנית",
  description: "תיאור",
  warmupText: "טקסט חימום",
};
/**
 * Edits the warmup copy on an ALREADY-ASSIGNED program — the text the trainee sees at the top of the
 * workout screen.
 *
 * Deliberately separate from ProgramSettingsDialog: that one posts three booleans keyed by
 * `client.id` to the trainee's ACTIVE program, while this posts free text keyed by `program.id`.
 * Combining them would mean two endpoints in one save (and partial-failure handling), and would lose
 * the ability to edit a historical program.
 *
 * Why this dialog needs to exist at all: the warmup text is copied from the template once, at
 * assignment time. Editing the template afterwards does NOT reach programs already assigned from it,
 * so this is the only way to change what a trainee is currently seeing.
 *
 * The `Warmup Text (English)` field it used to carry is gone: English is an `entity_translation`
 * row now, edited through the schema-driven panel below, which covers every enabled locale at once
 * instead of just the one. `warmupText` (the legacy English column) is still sent, unchanged, so an
 * installed mobile binary that sends no Accept-Language keeps reading what it read before.
 */

const ProgramWarmupDialog = ({isOpen, close, program, handleReload}) => {
  const {notificationHandler} = useMessageContext();

  const [warmupTextHeb, setWarmupTextHeb] = useState(program?.warmupTextHeb ?? "");
  // No input any more — carried so the legacy English column is passed through untouched.
  const [warmupText, setWarmupText] = useState(program?.warmupText ?? "");
  const [saving, setSaving] = useState(false);

  // Re-seed when the dialog opens or the caller swaps the program (the plans list can open this for
  // different rows without unmounting).
  useEffect(() => {
    if (program && isOpen) {
      setWarmupTextHeb(program.warmupTextHeb ?? "");
      setWarmupText(program.warmupText ?? "");
    }
  }, [program, isOpen]);

  const handleSave = () => {
    if (!program?.id) return;
    setSaving(true);
    // Sent as-is, with no ''->null cleanup: '' is meaningful here (clear the text so the app hides
    // the warmup body) and must not be collapsed into null ("leave this language untouched").
    updateProgramWarmup(program.id, {warmupText, warmupTextHeb})
      .then(() => {
        notificationHandler.success("טקסט החימום נשמר בהצלחה");
        handleReload?.();
        close();
      })
      .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)))
      .finally(() => setSaving(false));
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      '& fieldset': {borderColor: '#E2E8F0'},
      '&:hover fieldset': {borderColor: '#CBD5E1'},
      '&.Mui-focused fieldset': {borderColor: '#57837E'}
    }
  };

  return (
    <Dialog
      title={
        <Box sx={{textAlign: "center"}}>
          <Typography sx={{fontSize: "22px", fontWeight: 600, color: "#57837E", mb: 0.5}}>
            טקסט חימום
          </Typography>
          <Typography sx={{fontSize: "14px", color: "#64748B", fontWeight: 400}}>
            הטקסט שהמתאמן רואה בראש מסך האימון בתוכנית הזו.
          </Typography>
        </Box>
      }
      close={close}
      isOpen={isOpen}
      showCloseButton={true}
      narrow={true}
      actions={
        <Box sx={{display: "flex", gap: 2, justifyContent: "center"}}>
          <Button
            onClick={close}
            variant="outlined"
            sx={{
              color: "#64748B",
              borderColor: "#E2E8F0",
              px: 4,
              "&:hover": {borderColor: "#CBD5E1", backgroundColor: "#F8FAFC"}
            }}
          >
            ביטול
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: "#57837E",
              px: 4,
              "&:hover": {backgroundColor: "#456b67"}
            }}
          >
            שמור
          </Button>
        </Box>
      }
    >
      <Box sx={{direction: "rtl", p: 2}}>
        <Typography sx={{fontSize: "13px", color: "#64748B", lineHeight: 1.6, mb: 2}}>
          עריכה כאן משפיעה על התוכנית הזו בלבד. כדי לשנות את ברירת המחדל לתוכניות עתידיות, ערוך את
          התבנית. השארת השדה ריק תסתיר את טקסט החימום מהמתאמן.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="טקסט חימום (עברית)"
          value={warmupTextHeb}
          onChange={(e) => setWarmupTextHeb(e.target.value)}
          inputProps={{maxLength: 4000}}
          sx={{...fieldSx, mb: 2}}
        />

        <TranslationDialogButton
          type="PROGRAM"
          entityId={program?.id ?? null}
          fieldLabels={PROGRAM_FIELD_LABELS}
          entityNoun="התוכנית"
          dialogTitle="תרגומי התוכנית"
        />
      </Box>
    </Dialog>
  );
};

export default ProgramWarmupDialog;
